import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { usePublicBasketField } from "@shared/modules/basket/services/useBasketField";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import PurchaserInfo from "@app-contexts/public/shared/checkout/components/PurchaserInfo/PurchaserInfo.web";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import { useFormContext } from "react-hook-form";
import { useCallback } from "react";
import type { CheckoutFormData } from "../../hooks/useCheckoutValidation";
import { VALIDATION_FIELDS } from "../../hooks/useCheckoutValidation";

/**
 * Purchaser Info Section
 *
 * This section manages purchaser information (name, email, phone) validation.
 * It uses React Hook Form context to update validation state in a unidirectional manner.
 */
export function PurchaserInfoSection({
  publicBasket,
  venueData,
}: {
  publicBasket: ReturnType<typeof usePublicBasket>;
  venueData: PublicVenueSerializer | undefined;
}) {
  const { setValue, watch, formState } = useFormContext<CheckoutFormData>();
  const nameOnTicket = usePublicBasketField("name_on_ticket");
  const email = usePublicBasketField("email");
  const phoneNumber = usePublicBasketField("phone_number");
  const subscribeToCompany = usePublicBasketField("subscribe_to_company");
  const subscribeToVenue = usePublicBasketField("subscribe_to_venue");

  const isValid = watch(VALIDATION_FIELDS.purchaserInfo);
  const isTouched = formState.touchedFields.purchaserInfo ?? false;
  const validationAttempted = watch(VALIDATION_FIELDS.validationAttempted);
  const errorMessage = formState.errors.purchaserInfo?.message;

  /**
   * Handle validation changes from the PurchaserInfo component.
   * Updates the form context instead of calling parent callbacks.
   * This maintains unidirectional data flow.
   */
  const handleValidationChange = useCallback(
    (isValid: boolean) => {
      setValue(VALIDATION_FIELDS.purchaserInfo, isValid);
    },
    [setValue]
  );

  // Show error when field is touched OR validation attempted (hybrid approach)
  const shouldShowError =
    (isTouched || validationAttempted) && !isValid && Boolean(errorMessage);

  return (
    <Box>
      <PurchaserInfo
        nameOnTicket={nameOnTicket}
        email={email}
        phoneNumber={phoneNumber}
        venueData={venueData}
        subscribeToCompany={subscribeToCompany}
        subscribeToVenue={subscribeToVenue}
        onSubmit={(vals) => {
          publicBasket.updatePurchaserInfo({
            name_on_ticket: vals.nameOnTicket,
            email: vals.email,
            phone_number: vals.phoneNumber,
            subscribe_to_company: vals.subscribeToCompany,
            subscribe_to_venue: vals.subscribeToVenue,
          });
        }}
        onValidationChange={handleValidationChange}
      />

      {/* Display inline error from RHF when validation attempted and invalid */}
      {shouldShowError && (
        <Box mt="xs">
          <Text fontSize="sm" color="red.500" role="alert">
            {errorMessage}
          </Text>
        </Box>
      )}
    </Box>
  );
}
